import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Star, Store } from "lucide-react";
import { errorMessage } from "@web/lib/api";
import { useDebounced } from "@web/lib/useDebounced";
import { bookingVendorApi, vendorsQuery, type Vendor } from "@web/lib/queries-vendors";
import {
  BottomSheet, Button, EmptyState, Inline, Row, RowGroup, SearchBar, Skeleton, Stack, StatusChip,
  Text, TextField, useToast,
} from "@web/ui";
import { coiLabel, coiTone } from "../vendor/VendorRow";

/** The search-first picker SPEC §3.11 asks for: preferred at this venue first,
 * then everybody else, with "+ New vendor" for the one that is not in the
 * directory yet.
 *
 * Two sections rather than three. The spec names "Preferred here / Approved /
 * All", but with no row in `vendor_venue_prefs` meaning "approved", the second
 * and third are the same list — so showing both would be showing everybody
 * twice. The heading says which is which.
 */
export function VendorPickerSheet({ bookingId, venueId, attachedVendorIds, onClose }: {
  bookingId: string;
  venueId: string | null;
  attachedVendorIds: string[];
  onClose: () => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const q = useDebounced(draft);

  const list = useQuery(vendorsQuery({ ...(q ? { q } : {}), ...(venueId ? { venueId } : {}) }));
  const rows = (list.data ?? []).filter(vendor => !attachedVendorIds.includes(vendor.id));
  const groups = useMemo(() => {
    const preferred = rows.filter(vendor => vendor.standing === "preferred");
    const rest = rows.filter(vendor => vendor.standing !== "preferred");
    return [
      ...(preferred.length ? [{ label: "Preferred here", rows: preferred }] : []),
      ...(rest.length ? [{ label: preferred.length ? "Everybody else" : "Your vendors", rows: rest }] : []),
    ];
  }, [rows]);

  async function attach(vendor: Vendor) {
    setBusy(true);
    try {
      await bookingVendorApi.attach(bookingId, { vendorId: vendor.id });
      toast.success(`${vendor.name} added to the event`);
      onClose();
    } catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  }

  async function createAndAttach() {
    setBusy(true);
    try {
      const created = await bookingVendorApi.createAndAttach(bookingId, { name: name.trim() });
      toast.success(`${created.name} added to the directory and to the event`);
      onClose();
    } catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  }

  if (creating) {
    return (
      <BottomSheet open onClose={() => setCreating(false)} title="New vendor"
        footer={<Button loading={busy} disabled={!name.trim()} onClick={() => void createAndAttach()}>Add and put on this event</Button>}>
        <Stack gap={3}>
          <TextField label="Business name" value={name} onChange={event => setName(event.target.value)} autoFocus maxLength={160} />
          <Text variant="caption" tone="tertiary">
            They go into the directory as well, so the next booking can find them. Add their number and certificate from More › Vendors.
          </Text>
        </Stack>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet open onClose={onClose} size="large" title="Add a vendor">
      <Stack gap={3}>
        <SearchBar value={draft} onChange={setDraft} placeholder="Search your vendors" label="Search your vendors" />
        {list.isError
          ? <EmptyState icon={Store} title="Vendors could not be loaded" body={errorMessage(list.error)}
              action={<Button onClick={() => void list.refetch()}>Try again</Button>} />
          : !list.data ? <Skeleton count={4} />
            : groups.length === 0
              ? <EmptyState icon={Store} title={q ? "No matches" : "Nobody left to add"}
                  body={q ? "Nothing in the directory matches that." : "Every vendor in your directory is already on this event."}
                  action={<Button fullWidth={false} icon={<Plus aria-hidden size={18} />}
                    onClick={() => { setName(draft); setCreating(true); }}>New vendor</Button>} />
              : <Stack gap={3}>
                {groups.map(group => (
                  <Stack key={group.label} gap={2}>
                    <Text as="h3" variant="title">{group.label}</Text>
                    <RowGroup>
                      {group.rows.map(vendor => (
                        <Row key={vendor.id} wrap
                          icon={vendor.standing === "preferred" ? <Star aria-label="Preferred here" size={18} /> : undefined}
                          title={vendor.name}
                          subtitle={[vendor.categoryName, vendor.contactName, vendor.phone].filter(Boolean).join(" · ") || "No contact details yet"}
                          trailing={<StatusChip tone={coiTone(vendor.coiStatus)}>{coiLabel(vendor.coiStatus)}</StatusChip>}
                          disabled={busy}
                          onClick={() => void attach(vendor)} />
                      ))}
                    </RowGroup>
                  </Stack>
                ))}
                <Inline justify="between" align="center" wrap>
                  <Text variant="caption" tone="tertiary">Not in the list?</Text>
                  <Button variant="secondary" fullWidth={false} icon={<Plus aria-hidden size={18} />}
                    onClick={() => { setName(draft); setCreating(true); }}>New vendor</Button>
                </Inline>
              </Stack>}
        <Text variant="caption" tone="tertiary">
          A vendor whose certificate is on file and still valid counts as received on this booking straight away.
        </Text>
      </Stack>
    </BottomSheet>
  );
}
